/* Wiki content (English source). Each guide is translated under wiki.<id>.title / .summary / .content
 * in every locale; this file is the English default.
 * Content format (rendered by Wiki.js):
 *   ## Heading            → a section (listed in "On this page")
 *   - item / 1. step      → bullet / numbered step
 *   > tip: text / > warn: text → callout box
 *   **bold**, `code`      → inline
 */

export const WIKI_GROUPS = [
  { id: 'start', title: 'Get started' },
  { id: 'matches', title: 'Matches' },
  { id: 'competition', title: 'Teams & competition' },
  { id: 'servers', title: 'Servers' },
  { id: 'live', title: 'Live & broadcast' },
  { id: 'integrations', title: 'Integrations' },
];

export const WIKI_GUIDES = [
  // ── Get started ──
  {
    id: 'getting-started', group: 'start', icon: 'rocket', title: 'Quick start',
    summary: 'From an empty panel to a live match in five steps.',
    content: `## Steps
1. **Add a server** - Servers → **+ Add Server**: IP, port and RCON password.
2. **Create two teams** - Teams → add each player's Steam ID or profile link. Names are fetched from Steam.
3. **Create a match** - Matches → **+ Create Match**: pick both teams, the server, BO1/BO3/BO5 and the maps.
4. **Players join** - they connect with the command shown on the match page and type \`.ready\`.
5. **Play** - score, kills and stats update live on the match page.

## Next
- Save your usual settings in a **Season** so new matches fill themselves in.
- Add SFTP to your server for backups, demos and server migration.
> tip: Everything works without plugins - the panel only uses RCON and the server's own log stream.`,
  },
  {
    id: 'concepts', group: 'start', icon: 'book', title: 'How Flux works',
    summary: 'RCON to control the server, logs to follow the game - nothing to install on the game server.',
    content: `## Two connections
- **RCON** - the panel sends commands to the game server: configs, pauses, team names, backups.
- **Logs** - the game server sends every event (kills, rounds, chat) to the panel's log receiver on port \`3333\`.

## Why it matters
- No mods or plugins - CS2 updates don't break anything.
- A normal CS2 dedicated server is all you need.

## Nothing gets lost
- The log receiver runs separately from the panel. If the panel restarts, logs wait in a queue and are processed when it is back.
> warn: Port 3333 must be reachable from your game servers. With Cloudflare, set that record to **DNS only** (grey cloud).`,
  },

  // ── Matches ──
  {
    id: 'matches', group: 'matches', icon: 'swords', title: 'Create a match',
    summary: 'Pick teams, server, format and maps - the server is set up for you.',
    content: `## Create
1. Matches → **+ Create Match**.
2. Choose both teams and a free server.
3. Choose **BO1, BO3 or BO5**, then the maps and starting sides, or a knife round for sides.
4. Optional: pick a **Season** to fill in all settings at once, or turn on **Map veto**.
5. Click **Create** - the panel configures the server and puts it in warmup.

## Match list
- **HLTV view** (default): one row per match, grouped by day, live matches on top. **Cards** shows a grid.
- **Mine / All** filters by who created the match; search finds teams.
- The trash icon deletes a finished match; the × cancels a pending one.

## Status
- **Pending** → created, warmup. **Live** → being played. **Finished** → done. **Cancelled** → stopped by an admin.`,
  },
  {
    id: 'ready-start', group: 'matches', icon: 'check', title: 'Ready & start',
    summary: 'How teams ready up and when a map starts.',
    content: `## When is a team ready?
- When **5 of its players** typed \`.ready\`, **or** one player typed \`.forceready\` for the whole team.
- If any player types \`.unready\`, their team is **no longer ready**.

## When does the map start?
- When **both teams** are ready (or 10 players are ready).
- If the match uses **Admin only start**, chat says "Both teams are ready, waiting for the Admin" - start it with **Go Live** in the panel or \`.start\` in game.

## BO3 / BO5
- Every map has its own ready status. After a map ends, the next map loads and **everyone starts unready**.
- While the next map is loading, ready commands are not counted - players are told to wait.
> tip: A player on the wrong side gets a message telling them which side to join; they aren't marked ready until they switch.`,
  },
  {
    id: 'in-game-commands', group: 'matches', icon: 'terminal', title: 'In-game commands',
    summary: 'Chat commands for players and admins. Both ! and . work.',
    content: `## Players
- \`.ready\` / \`.r\` - mark yourself ready (warmup).
- \`.unready\` / \`.ur\` - not ready; your team becomes unready.
- \`.forceready\` - ready your whole team.
- \`.stay\` / \`.switch\` - after winning the knife round.
- \`.tac\` - tactical timeout.
- \`.tech\` - technical pause; both teams type \`.unpause\` to continue.
- \`.stop\` - ask to stop the round; one player of each team must agree.
- \`.pause\` - shows the pause commands.
- \`.fixwarmup\` - runs the warmup config again.

## Admins
- \`.fp\` / \`.fup\` - force pause / unpause (only an admin can unpause).
- \`.pfp\` - production pause.
- \`.start\` - start without waiting for ready.
- \`.restore 5\` - go back to the end of round 5.
- \`.asay text\` - message to everyone as [Admin].
> tip: Commands are limited to 5 per second per match, and are blocked during halftime.`,
  },
  {
    id: 'match-controls', group: 'matches', icon: 'sliders', title: 'Match controls',
    summary: 'The buttons on the match page and what each one does.',
    content: `## Match flow and pauses
- **Warmup / Go Live / Knife Round** - move the match between stages.
- **Pause** - admin pause; only an admin can unpause. **Prod Pause** - for production, with reminders. **Unpause** - resume.

## Server
- **Migrate Server** - move the match to another server and continue from the current round.
- **Rehost** - reload the current map on the same server and continue from any score.

## Danger zone
- **Restore Backup** - go back to a round of the current map.
- **Forfeit Match** / **Cancel** - end the match with a winner, or cancel it.

## Map controls (per map)
- **Force Teams** - put players on their correct sides again (warmup only).
- **Swap Team** - switch sides before any round is played.
- **Reset Map** - restart the map at 0-0.
- **Forfeit Map** - give the map to a team.
- **Replay** - play this map again from a round you choose (see Map replay).

## Players
- **Player Management** adds a player to this match only - they are not added to the team.
- **Tech pause availability** blocks \`.tech\` for a team, and sets a time budget per team.`,
  },
  {
    id: 'map-replay', group: 'matches', icon: 'replay', title: 'Map replay',
    summary: 'Play any map again from a round you choose - also an earlier map, or a finished match.',
    content: `## When to use it
- A team lost connection at 12-11, the map ended 13-11 and the next map already loaded.
- A finished match has to be continued.

## Steps
1. Match page → **Map controls** → **Replay** on the map.
2. Pick the round to go back to (e.g. round 23 = 12-11), or **Round 0** to start the map at 0-0.
3. Read what will happen and confirm.
4. The server loads that map in warmup. When you start, the round's backup is loaded - same score and money.

## What happens
- Rounds and stats after that point are voided (kept for audit).
- **Later maps are voided too**; the series score is recalculated and those maps are played again afterwards.
- A finished match is reopened.
> warn: A finished match can't be reopened if its server is now hosting another match, or if a bracket match it led to has already started.`,
  },
  {
    id: 'backups', group: 'matches', icon: 'drive', title: 'Backups & restore',
    summary: 'Every round is backed up; go back to any round of the current map.',
    content: `## Automatic
- The game server writes a backup after every round. With SFTP, the panel keeps its own copy too.

## Restore from the panel
1. Match page → **Restore Backup**.
2. Pick a round of the **current map**.
3. The match is paused; both teams type \`.unpause\` to continue. Score and stats go back to that round.

## Restore in game
- Admins type \`.restore 5\` to go back to the end of round 5.

## Other tools
- **Rehost** - reload the map and continue from a score.
- **Map replay** - go back to an earlier map.
> tip: The **Restore checks** in the Logs tab show, per round, whether damage was dealt before someone asked for a restore.`,
  },
  {
    id: 'veto', group: 'matches', icon: 'map', title: 'Map veto',
    summary: 'Teams ban and pick maps from their own links; the match loads itself when they finish.',
    content: `## Set up
1. Turn on **Map veto** when creating the match and pick a format (VRS or FACEIT, BO1/BO3/BO5).
2. Share the **Team 1** and **Team 2** links with the captains. The **Admin** link can act for both.

## During the veto
- Optional **coin flip**: both teams click Ready, Team A calls heads or tails, the winner chooses who starts.
- Press and **hold** a map to ban or pick it.
- After a pick, the other team chooses CT or T. The last map is played with a knife round.

## Afterwards
- Maps and sides are applied to the match and the server loads the first map.
- Anyone with the room link without a key watches as a spectator.
> tip: **Reset Veto** starts the veto over - until the match is finished or cancelled.`,
  },
  {
    id: 'match-access', group: 'matches', icon: 'key', title: 'Match access',
    summary: 'Connect info and private links for players, coaches and tools.',
    content: `## Server
- The connect command, with the IP and password hidden until you click the eye. **Copy** always copies the full command.
- **SDR** - the Steam relay address, if the server has one.

## Coach tablets
- One link per team. A coach opens it on a tablet to call a tactical timeout - the same as \`.tac\`.
- **Regenerate** makes new links; the old ones stop working.

## Log stream
- A private link that streams every raw server log line of the match.
- **Open live preview** shows the stream in a new tab.
> tip: **Hide** collapses the card; your choice is remembered.`,
  },

  // ── Teams & competition ──
  {
    id: 'teams', group: 'competition', icon: 'users', title: 'Teams & players',
    summary: 'Create teams, add players, import many teams at once.',
    content: `## Create a team
1. Teams → **+ Create Team**: name and tag.
2. Add players by Steam ID or profile link - any format works.
3. Names are fetched from Steam. If that fails, type the name yourself.

## Bulk add
- **Upload** a CSV or Excel file: column A = team name, then one player per column (5 main + up to 2 subs).
- **Or paste** one team per line: \`TeamName player1 player2 …\`.
- A preview shows problems before anything is imported.

## Extras
- Upload a team logo and player avatars.
- Players can be **main** or **sub**.
- With a FACEIT API key, each player shows a level + ELO badge.
> warn: Custom in-game avatars need a \`default.png\` (64×64, under 16 KB) in the avatars folder of the game server.`,
  },
  {
    id: 'seasons', group: 'competition', icon: 'clipboard', title: 'Seasons',
    summary: 'A season holds your match settings and groups matches, leaderboard and brackets.',
    content: `## Create
1. Seasons → **+ Create Season**: name, dates, mode and format.
2. You land on the season's **Settings** tab to fill in everything else.

## The season page
- **Overview** - matches, standings and quick actions.
- **Matches**, **Leaderboard**, **Brackets** - everything in this season.
- **Settings** - match rules, veto, broadcast, chat prefix, temp admins, CVARs, halftime, TeamSpeak and more. Save with the bar at the bottom.

## Using it
- **Add match** on the season page opens Create Match with the season filled in.
- **Temp admins** set here are added to every match of the season as spectator + in-game admin.`,
  },
  {
    id: 'brackets', group: 'competition', icon: 'trophy', title: 'Brackets',
    summary: 'Single elimination, double elimination or group stage - winners advance on their own.',
    content: `## Create
1. Season page → **Brackets** → **+ Create Bracket**.
2. Pick the teams and the type, set the seeding, then **Start Tournament**.

## Types
- **Single elimination** - lose once and you're out. Optional 3rd place match.
- **Double elimination** - winners and losers bracket, grand final, optional reset.
- **Group stage** - groups play GSL, single or double elimination.

## Playing
- **Schedule** on a slot with both teams creates the match on a server you pick, with the season's settings.
- When the match finishes, the winner advances by itself.
- Each round can have its own format (e.g. BO1 groups, BO3 semis, BO5 final).
> tip: Every bracket has a public page that updates live.`,
  },
  {
    id: 'maps', group: 'competition', icon: 'map', title: 'Map pool',
    summary: 'Your own list of maps for match creation and veto.',
    content: `## Pools
- **Active Duty** - 7 maps, used by the veto formats.
- **Wingman** - 3, 5 or 7 maps for 2v2.
- **Competitive** - any number of maps, picked by hand.

## Adding maps
- Maps → **Quick Add** a preset, or add a map by name (e.g. \`de_mirage\`).
- Workshop maps work too. A map image is found automatically if you leave the URL empty.
> tip: Every admin has their own map pool.`,
  },
  {
    id: 'leaderboards', group: 'competition', icon: 'chart', title: 'Leaderboards & ratings',
    summary: 'Player stats across a season, rated with HLTV 2.0.',
    content: `## Where
- Season page → **Leaderboard**, and the public season page.

## Stats
- Kills, deaths, assists, ADR, HS%, KAST, entry and trade kills, multi-kills and clutches.
- **HLTV 2.0** rating (an estimate of HLTV's Rating 2.0, the same formula on every match).

## Terms
- **ADR** - average damage per round.
- **KAST** - % of rounds with a kill, assist, survival or being traded.
- **Entry kill** - the first kill of a round.`,
  },
  {
    id: 'vrs-rankings', group: 'competition', icon: 'globe', title: 'VRS rankings',
    summary: "Valve's official regional standings, inside the panel.",
    content: `## What it is
- Official **Valve Regional Standings** data, published weekly by Valve.

## Using it
- VRS Rankings in the sidebar. Filter by region (Global, Europe, Americas, Asia), year and week.
- Search by team or player name.`,
  },

  // ── Servers ──
  {
    id: 'servers', group: 'servers', icon: 'server', title: 'Game servers',
    summary: 'Add your CS2 servers and keep an eye on them.',
    content: `## Add a server
1. Servers → **+ Add Server**.
2. Enter name, IP, port (default 27015) and RCON password. The password is stored encrypted.
3. Click **RCON** on the card to test the connection.

## Good to know
- A server shows **In Use** while a match runs on it; new matches only use free servers.
- **Public IP** (optional) is the address shown to players, e.g. a LAN address. RCON and logs always use the main IP.
- Super admins edit warmup, knife and live configs in **Settings**; **Config** on the card uploads them (needs SFTP).`,
  },
  {
    id: 'ftp-sftp', group: 'servers', icon: 'folder', title: 'SFTP / FTP & files',
    summary: "Give the panel access to the server's files for backups, demos and migration.",
    content: `## Set up
1. Edit the server → **Backup protocol**: SFTP (recommended) or FTP.
2. Host, port, username, password and the **remote path** to the \`csgo\` folder.
3. **Test connection**, then save.

## What you get
- Round backups copied to the panel, restores on any server, automatic migration, demo downloads.

## File manager
- Server card → **Manage** → **File Manager**: browse, upload, download, rename and delete files in the browser.
> warn: Changing files on a server with a live match asks you to confirm first.`,
  },
  {
    id: 'lan', group: 'servers', icon: 'network', title: 'LAN servers',
    summary: 'Run venue servers without port forwarding - a small agent connects out to the panel.',
    content: `## Set up
1. Servers → **LAN Environments** → create an event.
2. Run the install command on every venue machine.
3. **Approve** each machine; its CS2 servers appear - click **Add**.

## Modes
- **RCON only** - commands and logs go through the agent.
- **Full access** - adds a private tunnel, so SFTP, the file manager and other services (e.g. TeamSpeak) work too.

## Maintenance
- \`sudo flux-agent update\` - update the agent.
- \`sudo flux-agent uninstall\` - remove it (your CS2 servers are not touched).
> warn: Full access needs UDP port 51820 open on the panel's server.`,
  },

  // ── Live & broadcast ──
  {
    id: 'public-view', group: 'live', icon: 'globe', title: 'Public pages & scoreboard',
    summary: 'What viewers see without logging in.',
    content: `## Public pages
- **Matches** - live and finished matches with scores and stats.
- **Brackets**, **season leaderboards** and **teams**.

## Scoreboard
- Live score, half scores, map tabs for BO3/BO5, player stats and a live kill feed.
- **Stream delay** holds back public updates for broadcasts.
- **Public kill feed** can be turned off per match.

## Demos
- Download a map's demo from the match page when it has been uploaded.`,
  },
  {
    id: 'damage-details', group: 'live', icon: 'shield', title: 'Restore checks',
    summary: 'Help deciding whether a round may be restored after a crash or disconnect.',
    content: `## What it shows
- For every round: whether damage was dealt, by whom, with what weapon and when.
- Chat messages asking for a restore (e.g. "restore", "crash", "dc", "lag").

## Where
- Match page → **Logs** tab → **Damage**.

## Reading it
- Restore asked **before any damage** → usually safe to restore.
- Damage already dealt → the round has been played; decide with care.`,
  },

  // ── Integrations ──
  {
    id: 'teamspeak', group: 'integrations', icon: 'mic', title: 'TeamSpeak coach mute',
    summary: 'Coaches are muted during live rounds and unmuted when they may talk.',
    content: `## Set up
1. Season → Settings → **Integrations** → TeamSpeak: host, query port (10011), query user, password, voice port.
2. **Test connection**.
3. On each team, set the coach's TeamSpeak name - it must match exactly.

## When coaches can talk
- Warmup, knife round, tactical timeouts and the halftime break (if turned on).
- Muted during live rounds and tech pauses.
> tip: Add the panel's IP to the TeamSpeak query allowlist so it isn't flood-banned.`,
  },
  {
    id: 'faceit', group: 'integrations', icon: 'star', title: 'FACEIT',
    summary: 'Show FACEIT level and ELO next to players.',
    content: `## Set up
- Settings → **FACEIT Integration** → paste the API key → **Test Key** → save.

## Where it shows
- Team rosters, team lists (average ELO), the match page and the veto room.
> tip: If FACEIT limits the number of requests, the panel waits a minute and fills the ELO in afterwards.`,
  },
  {
    id: 'external-tournaments', group: 'integrations', icon: 'trophy', title: 'External tournaments',
    summary: "Follow matches on someone else's servers using only their logs.",
    content: `## How it works
- Each tournament has one log address. Point all the organiser's servers at it.
- The panel recognises which match each server is playing (by team names and Steam IDs) and follows it live.

## Set up
1. Sidebar → **Tournaments** → create a tournament.
2. Add teams with their players, then create matches - by hand or through the **veto link**.
3. Give the log address to the server admins.

## Features
- Live scoreboard, events, stats and seating for the stage.
- **Upload a map's log file** when the live log was missing or broken.
- API, live streams and signed webhooks for overlays and bots.`,
  },
  {
    id: 'api', group: 'integrations', icon: 'code', title: 'API & keys',
    summary: 'Read match data from your own tools.',
    content: `## Keys
1. Sidebar → **API** → create a key (up to 5).
2. Send it as \`Authorization: Bearer <key>\` or \`X-API-Key\`.
- A key has the same access as the admin who created it.

## Docs
- \`/api/docs\` - the full API guide.
- \`/api-tester\` - try every endpoint in the browser.
> warn: Keep keys secret. Revoke a key you no longer use.`,
  },
];
